import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ScopedAuditTable, type AuditRow } from '../../components/scoped/ScopedAuditTable';
import { PLATFORM_AUDIT_DESCRIPTOR } from '../../components/scoped/scopeDescriptors';
import { platformApi } from '../../api/endpoints/platform';
import { shortId } from '../../lib/shortId';

/**
 * Auditoría del módulo Platform. Monta el motor compartido `ScopedAuditTable` con
 * `PLATFORM_AUDIT_DESCRIPTOR` (query = platformApi.queryAudit, sin scopeRef) e inyecta la resolución de
 * nombres de actor/target con las listas a nivel plataforma (admins/tenants/roles). Antes esta vista
 * era un componente `PlatformAuditTable` que clonaba toda la maquinaria de la tabla (DEC-028).
 */
function PlatformAuditPage() {
    // El audit log NO guarda el nombre del actor (solo actorCognitoSub). Resolvemos con la lista de
    // admins PLATFORM (best-effort); si el actor no está, cae al email o a un sub corto. El mismo mapa
    // resuelve el target de tipo PlatformUser.
    const { data: adminsData } = useQuery({
        queryKey: ['platform', 'users', 'audit-actor-map'],
        queryFn: () => platformApi.listPlatformAdmins(),
    });
    const adminBySub = useMemo(() => {
        const map = new Map<string, { displayName: string; email: string }>();
        for (const u of adminsData?.items ?? [])
            map.set(u.cognitoSub, { displayName: u.displayName, email: u.email });
        return map;
    }, [adminsData]);

    // Mapas para resolver el Target a nombres legibles (en vez del UUID): tenants + roles platform.
    const { data: tenantsData } = useQuery({
        queryKey: ['platform', 'tenants', 'audit-target-map'],
        queryFn: () => platformApi.listTenants(),
    });
    const { data: rolesData } = useQuery({
        queryKey: ['platform', 'roles', 'audit-target-map'],
        queryFn: () => platformApi.listPlatformRoles(),
    });
    const tenantNameById = useMemo(() => {
        const map = new Map<string, string>();
        for (const t of tenantsData?.items ?? []) map.set(t.tenantId, t.displayName);
        return map;
    }, [tenantsData]);
    const roleNameById = useMemo(() => {
        const map = new Map<string, string>();
        for (const r of rolesData?.items ?? []) map.set(r.roleId, r.displayName);
        return map;
    }, [rolesData]);

    const resolveActor = useCallback(
        (row: AuditRow): string => {
            const sub = row.actor?.cognitoSub;
            const admin = sub ? adminBySub.get(sub) : undefined;
            return (
                admin?.displayName || row.actor?.email || admin?.email || (sub ? shortId(sub) : '—')
            );
        },
        [adminBySub],
    );

    // Target legible: resuelve el id de la entidad al nombre según su tipo. Tipos de plataforma:
    // Tenant → nombre del tenant; PlatformUser → nombre del admin; Role → nombre del rol. Tipos
    // desconocidos → `Tipo: <id corto>` (NUNCA un UUID crudo pelado, siempre etiquetado y acortado).
    const resolveTarget = useCallback(
        (row: AuditRow): string => {
            const type = row.target?.type ?? '';
            const id = row.target?.id ?? '';
            if (!type && !id) return '—';
            if (type === 'Tenant') return `Tenant: ${tenantNameById.get(id) ?? shortId(id)}`;
            if (type === 'PlatformUser')
                return `Usuario: ${adminBySub.get(id)?.displayName ?? shortId(id)}`;
            if (type === 'Role') return `Rol: ${roleNameById.get(id) ?? shortId(id)}`;
            return `${type}: ${shortId(id)}`;
        },
        [adminBySub, tenantNameById, roleNameById],
    );

    return (
        <ScopedAuditTable
            descriptor={PLATFORM_AUDIT_DESCRIPTOR}
            resolveActor={resolveActor}
            resolveTarget={resolveTarget}
        />
    );
}

export const Route = createFileRoute('/_authenticated/_layout/platform/audit')({
    component: PlatformAuditPage,
});
